import React, { useCallback, useEffect } from 'react'
import {
  actionTypeWrapper,
  CloudAccountChangeV2,
  CloudChangeV2,
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
  setSelectedAccount,
  stopLoading,
  useAccountId,
  useCloudAccounts,
  useCurrentCloudProvider,
  useUserType,
  type CloudAccountChangeParams,
  type CustomCloudOptions,
  LoadingTypes,
  isLoading as Loading,
} from '@labrav/common'
import { ButtonV2, VerticalDivider } from '@labrav/react-components'
import { CircularProgress, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBadgeCheck } from '@fortawesome/pro-regular-svg-icons'
import { useFlagValue } from '@labrav/flags'
import { isEmpty } from 'lodash'
import { useNavigate, useParams } from 'react-router-dom'
import { FlyoutBlockPage } from '../../common/components/FlyoutBlockPage'
import { MarketplaceOnboardingPage } from '../../common/components/MarketplaceOnboardingPage'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { getMarketplaceReferralTemplatesInfo } from '../../modules/marketplaceMappingManager/action'
import {
  clearProductsListing,
  getProducts,
  setSelectedProductOnListing,
} from '../../modules/productsListing/action'
import { setCloudMarketplaceProductListing } from '../../modules/productListingFilter/action'
import { RootState, useDispatch, useSelector } from '../../store'
import type { FlyOutListingData } from '../../types/flyout'
import { CloudType } from '../../types/external'
import { CRMTypes } from '../../utils/constants'
import EmptyActivityLog from '../../common/images/EmptyActivityLog.png'
import HubspotImage from '../../common/images/HubSpot_Logo.svg'
import SalesforceImage from '../../common/images/salesforce.svg'
import {
  MarketplaceCreateTemplate,
  MarketplaceEmptyStateMappingManager,
} from './MarketplaceEmptyStateMappingManager/MarketplaceEmptyStateMappingManager'
import { MarketplaceHelpAndFaqData } from './MarketplaceHelpAndFaq/MarketplaceHelpAndFaq'
import { MarketplaceMappingManagerTemplate } from './MarketplaceTemplate/MarketplaceMappingManagerTemplate'
import { useStyles } from './MarketplaceMappingManager.styles'
import { customerFormData } from './utils/customerFormData'
import { getCloudAccountsForMappingManager } from './utils/getCloudAccountsForMappingManager'
const getCustomCloudSelectorOptions = ({
  azureEnabled,
}: {
  azureEnabled: boolean
}): CustomCloudOptions => [
  { name: 'AWS', disabled: false },
  { name: 'GCP', disabled: true },
  { name: 'AZURE', disabled: !azureEnabled },
]

export type MarketplaceMappingManagerProps = {
  isCrmSelected?: boolean
  crmConnectionPage?: React.ReactNode
}

export const MarketplaceMappingManager: React.FC<
  MarketplaceMappingManagerProps
> = ({ isCrmSelected = true, crmConnectionPage }) => {
  const dispatch = useDispatch()
  const { classes } = useStyles()
  const navigate = useNavigate()
  const partnerInviteEnabled = useFlagValue('partnerInvite')
  const customerData = customerFormData()
  const subscribedProducts = customerData.subscribedProducts
  const { flyoutSellerInfo } = useSelector<{
    flyoutSellerInfo: FlyOutListingData
  }>(state => ({
    flyoutSellerInfo: state.flyoutSellerInfo,
  }))
  const partnerType = useUserType()
  const crmId = useSelector<string>(
    state =>
      (state.AccountData[partnerType]?.partnerData?.crmId as string) || ''
  )
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const templatesData = useSelector(
    (state: RootState) => state.marketplaceMappingManager.templateDetails
  )
  const { crmType } = customerFormData()
  const { FLYOUT: flyoutProduct } = subscribedProducts
  const isFlyoutSubscribed = !partnerInviteEnabled?.value || !!flyoutProduct
  const isFlyoutOnboarded = flyoutSellerInfo?.onboarded === true
  const activeProducts = useSelector<string[]>(
    state =>
      (state.AccountData.user.partnerData?.activeProducts as string[]) || []
  )
  const isTemplatesDataLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_REFERRAL_TEMPLATES)
  )

  const { value: isSalesforceMappingManagerEnabled } = useFlagValue(
    'mappingManagerMultipleApnSalesforce'
  )
  const { value: isAzureMarketplaceMappingManagerEnabled } = useFlagValue(
    'azureMarketplaceMappingManager'
  )
  const { value: isAzureHubspotMappingManagerEnabled } = useFlagValue(
    'azureHubspotMappingManager'
  )
  const {
    accountId: routeAccountId,
    cloudType: routeCloudType,
    partnerId: accountIdFromParams,
  } = useParams<{
    cloudType?: string
    accountId?: string
    partnerId?: string
  }>()
  const resolvedCloudType = (
    routeCloudType ||
    selectedCloudTypeProductListing ||
    'aws'
  ).toUpperCase() as CloudType
  const accountId = useAccountId({
    product: 'flyout',
    cloud: resolvedCloudType,
  })
  const marketplaceCloudAccounts = useCloudAccounts('flyout')
  const currentAccountId = routeAccountId || accountIdFromParams || accountId
  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    currentAccountId || '',
    'flyout',
    resolvedCloudType
  )
  const { products } = useFlyOutProductsBasedOnSelectedCloud()

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      const originalAccountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params
      const cloudPath = cloud.toLowerCase()

      // Navigate to the new route with cloudType and accountId
      const pathname = `/settings/${cloudPath}/${originalAccountId}/mappings/marketplace`
      navigate(pathname, { replace: true })

      await dispatch(stopLoading(LoadingTypes.GENERAL))
      await dispatch(
        setSelectedAccount(originalAccountId, 'flyout', cloud as CloudType)
      )
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(actionTypeWrapper(cloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(cloud))
      //To-Do:clear template data
      await dispatch(getProducts(originalAccountId, cloud))
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    },
    [dispatch, navigate, selectedCloudTypeProductListing, location.search]
  )
  const handleCompleteOnboarding = () => {
    !activeProducts?.includes('flyout')
      ? navigate('/product-selection')
      : navigate('/flyout/onboarding/company-information')
  }

  useEffect(() => {
    const templateType =
      crmType === CRMTypes.salesforce ? 'marketplace' : 'flyout'
    if (!isEmpty(crmId)) {
      dispatch(
        getMarketplaceReferralTemplatesInfo(
          crmId,
          templateType,
          crmType as string,
          currentAccountId,
          undefined,
          undefined,
          resolvedCloudType
        )
      )
    }
  }, [crmId, crmType, currentAccountId, resolvedCloudType])

  const fetchProducts = useCallback(async () => {
    await dispatch(
      getProducts(currentAccountId, selectedCloudTypeProductListing)
    )
  }, [currentAccountId, selectedCloudTypeProductListing])

  useEffect(() => {
    if (!isEmpty(currentAccountId) && crmType === CRMTypes.salesforce) {
      if (isEmpty(products)) {
        fetchProducts()
      }
    }
  }, [currentAccountId, selectedCloudTypeProductListing, crmType])

  if (!isFlyoutSubscribed) {
    return <FlyoutBlockPage showHeader={false} />
  }
  if (isFlyoutSubscribed && !isFlyoutOnboarded) {
    return (
      <MarketplaceOnboardingPage
        customButton={
          <>
            <ButtonV2
              styleType="primary"
              onClick={handleCompleteOnboarding}
              data-testid="cntinue-onboarding-button"
            >
              Continue onboarding
            </ButtonV2>
          </>
        }
      />
    )
  }
  if (
    isFlyoutSubscribed &&
    isFlyoutOnboarded &&
    crmType === CRMTypes.hubspot &&
    !isCrmSelected
  ) {
    return <>{crmConnectionPage}</>
  }
  return (
    <div
      className={classes.root}
      data-testid="marketplace-mapping-manager-container"
    >
      <div className={classes.formWidth}>
        <div className={classes.topContainer}>
          <div className={classes.imageContainer}>
            <img
              src={
                crmType === CRMTypes.hubspot ? HubspotImage : SalesforceImage
              }
              alt="Hubspot Icon"
              className={
                crmType === CRMTypes.hubspot
                  ? classes.hubspotIcon
                  : classes.salesforceIcon
              }
            />
            <VerticalDivider height="24px" />
            <MarketplaceHelpAndFaqData />
          </div>
          <div className={classes.cloudContainer}>
            <div>
              {(isSalesforceMappingManagerEnabled &&
                crmType === CRMTypes.salesforce) ||
              (isAzureHubspotMappingManagerEnabled &&
                crmType === CRMTypes.hubspot) ? (
                <div className={classes.accountSelectorWidth}>
                  <CloudAccountChangeV2
                    cloudAccounts={getCloudAccountsForMappingManager(
                      marketplaceCloudAccounts,
                      [
                        CloudType.GCP,
                        ...((
                          crmType === CRMTypes.hubspot
                            ? isAzureHubspotMappingManagerEnabled
                            : isAzureMarketplaceMappingManagerEnabled
                        )
                          ? []
                          : [CloudType.AZURE]),
                      ]
                    )}
                    selectedAccountId={selectedAccountIdWithCloud as string}
                    onChange={handleCloudAccountChange}
                    width={220}
                    size="small"
                  />
                </div>
              ) : (
                <CloudChangeV2
                  size={'small'}
                  customCloudOptions={getCustomCloudSelectorOptions({
                    azureEnabled:
                      crmType === CRMTypes.hubspot
                        ? !!isAzureHubspotMappingManagerEnabled
                        : !!isAzureMarketplaceMappingManagerEnabled,
                  })}
                />
              )}
            </div>
            <div>
              <MarketplaceCreateTemplate
                buttonDisable={isTemplatesDataLoading}
                selectedAccountIdWithCloud={selectedAccountIdWithCloud}
                handleCloudAccountChange={handleCloudAccountChange}
              />
            </div>
          </div>
        </div>
        <div className={classes.bottomContainer}>
          {isTemplatesDataLoading ? (
            <CircularProgress className={classes.loadingSpinner} />
          ) : templatesData.length === 0 ? (
            <MarketplaceEmptyStateMappingManager />
          ) : (
            <MarketplaceMappingManagerTemplate
              cloudType={resolvedCloudType as CloudType}
              accountId={currentAccountId}
            />
          )}
        </div>
      </div>

      {/* Activity log */}
      <div className={classes.activityLogWidth}>
        <div className={classes.rootContainer}>
          <div className={classes.headingContainer}>
            <div className={classes.titleContainer}>
              <div
                className={classes.titleIconContainer}
                data-testid="heading-icon"
              >
                <div className={classes.titleIcon}>
                  {' '}
                  <FontAwesomeIcon
                    icon={faBadgeCheck}
                    style={{ width: '18px', height: '14px' }}
                  />
                </div>
              </div>
              <Typography className={classes.heading} data-testid="box-heading">
                Activity log
              </Typography>
            </div>
          </div>
          <div className={classes.svgBackground}>
            <img
              src={EmptyActivityLog}
              alt="Labra Icon"
              className={classes.labraIcon}
            />
            <div className={classes.activityHeading}>
              Activities will appear here
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MarketplaceMappingManager
